import { Button, IconButton, Tag } from '@ads/components-react';
import {
  ArrowRightIcon,
  MapPinIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePersonAddresses } from '../../../../hooks/usePersonAddresses';
import type { Person } from '../../../../types/person';
import type { AddressFormValues, PersonFormValues, TabProps } from '../personFormSchema';
import { AddressModal, type AddressPrefix } from './AddressModal';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface AddressesTabProps extends Pick<TabProps, 'control'> {
  readonly person: Person | undefined;
  readonly setValue: UseFormSetValue<PersonFormValues>;
}

// ─── Mapeamento de tipo de endereço para EAddressType ────────────────────────

const ADDRESS_TYPE_MAP: Record<AddressPrefix, 1 | 3> = {
  personalAddress: 1,
  paymentAddress: 3,
};

// ─── Card de endereço ─────────────────────────────────────────────────────────

function AddressCard({
  prefix,
  label,
  person,
  control,
  onEdit,
  onClear,
  isDeleting,
}: {
  prefix: AddressPrefix;
  label: string;
  person: Person | undefined;
  control: TabProps['control'];
  onEdit: () => void;
  onClear: () => void;
  isDeleting: boolean;
}) {
  const { t } = useTranslation('personManagement');

  const addr = person?.[prefix];
  const zipCode = useWatch({ control, name: `${prefix}.zipCode` });
  const street = useWatch({ control, name: `${prefix}.street` });

  const streetDisplay = [addr?.street ?? street, addr?.placeNumber, addr?.neighborhood]
    .filter(Boolean)
    .join(', ');
  const cityDisplay = [addr?.cityName, addr?.stateName].filter(Boolean).join(', ');
  const zip = addr?.zipCode ?? zipCode ?? '';
  const hasData = !!streetDisplay || !!zip;

  if (!hasData) {
    return (
      <div className="flex flex-col gap-nano rounded-md border-hairline border-dashed border-ctx-layout-border bg-ctx-layout-body p-mini">
        <div className="flex items-center justify-between gap-xs">
          <Tag label={label} size="sm" status="default" custom={{ text: 'text-nowrap' }} />
        </div>
        <div className="flex flex-col items-center gap-xxs py-xs text-center">
          <span
            className="flex h-md w-md items-center justify-center rounded-pill bg-ctx-layout-spotlight text-ctx-content-title"
            aria-hidden="true"
          >
            <MapPinIcon className="h-xxs w-xxs" />
          </span>
          <div className="flex flex-col gap-quark">
            <p className="ts-heading-xxs text-ctx-content-title">
              {t('personForm.addressEmpty.title')}
            </p>
            <p className="ts-caption-xxs text-ctx-content-base">
              {t('personForm.addressEmpty.description')}
            </p>
          </div>
          <Button
            hierarchy="secondary"
            size="sm"
            type="button"
            leadingIcon={PlusIcon}
            onClick={onEdit}
          >
            {t('personForm.addressEmpty.action', { type: label })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-nano rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
      <div className="flex items-start justify-between gap-xs">
        <Tag label={label} size="sm" status="default" custom={{ text: 'text-nowrap' }} />
        <div className="flex items-center gap-quark">
          <IconButton
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('actions.edit')}
            icon={PencilSquareIcon}
            onClick={onEdit}
          />
          <IconButton
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('actions.delete')}
            icon={TrashIcon}
            onClick={onClear}
            isLoading={isDeleting}
          />
        </div>
      </div>
      <div className="flex flex-col gap-quark">
        <p className="ts-paragraph-xs text-ctx-content-title">{streetDisplay || '—'}</p>
        <div className="flex flex-wrap items-center gap-xs ts-caption-xxs text-ctx-content-base">
          {zip && (
            <span>
              {t('personForm.address.zipCode')}: {zip}
            </span>
          )}
          {cityDisplay && (
            <>
              <span aria-hidden="true" className="text-ctx-content-disabled">
                ·
              </span>
              <span>{cityDisplay}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

const EMPTY_ADDRESS: AddressFormValues = {
  street: '',
  zipCode: '',
  placeNumber: '',
  neighborhood: '',
  complement: '',
  countryId: 1,
  stateId: undefined,
  cityId: undefined,
  stateName: null,
  cityName: null,
};

/**
 * Tab de endereços da pessoa. Exibe os dois tipos de endereço (pessoal e
 * cobrança) e permite criar, editar e remover via API. Inclui ação de copiar
 * endereço pessoal para cobrança.
 */
export function AddressesTab({ control, person, setValue }: AddressesTabProps) {
  const { t } = useTranslation('personManagement');

  const [modalPrefix, setModalPrefix] = useState<AddressPrefix | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const addresses = usePersonAddresses(person?.id ?? 0);

  const openModal = (prefix: AddressPrefix) => {
    setModalPrefix(prefix);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const syncFormState = (prefix: AddressPrefix, values: AddressFormValues) => {
    const fields = Object.keys(values) as Array<keyof AddressFormValues>;
    for (const field of fields) {
      setValue(`${prefix}.${field}`, values[field], { shouldDirty: false });
    }
  };

  const clearFormState = (prefix: AddressPrefix) => {
    const fields: Array<keyof AddressFormValues> = [
      'street',
      'zipCode',
      'placeNumber',
      'neighborhood',
      'complement',
      'stateId',
      'cityId',
    ];
    for (const field of fields) {
      setValue(`${prefix}.${field}`, undefined, { shouldDirty: false });
    }
  };

  const handleSave = async (prefix: AddressPrefix, values: AddressFormValues) => {
    if (person?.id) {
      const existing = person[prefix];
      const payload = {
        street: values.street ?? null,
        zipCode: values.zipCode ?? null,
        placeNumber: values.placeNumber ?? null,
        neighborhood: values.neighborhood ?? null,
        complement: values.complement ?? null,
        countryId: values.countryId ?? 1,
        stateId: values.stateId ?? null,
        cityId: values.cityId ?? null,
        stateName: values.stateName ?? null,
        cityName: values.cityName ?? null,
      };

      if (existing?.addressPersonId) {
        await addresses.updateAddress(existing.addressPersonId, payload);
      } else {
        await addresses.createAddress({
          ...payload,
          type: ADDRESS_TYPE_MAP[prefix],
        });
      }
    }

    syncFormState(prefix, values);
  };

  const handleClear = async (prefix: AddressPrefix) => {
    if (person?.id) {
      const existing = person[prefix];
      if (existing?.addressPersonId) {
        await addresses.deleteAddress(existing.addressPersonId);
      }
    }
    clearFormState(prefix);
  };

  const handleCopyToPayment = async () => {
    if (!person?.id) return;
    await addresses.copyPersonalToPayment();
  };

  const getDefaultValues = (prefix: AddressPrefix): AddressFormValues => {
    const addr = person?.[prefix];
    if (!addr) return EMPTY_ADDRESS;
    return {
      street: addr.street ?? '',
      zipCode: addr.zipCode ?? '',
      placeNumber: addr.placeNumber ?? '',
      neighborhood: addr.neighborhood ?? '',
      complement: addr.complement ?? '',
      countryId: addr.countryId ?? 1,
      stateId: addr.stateId ?? undefined,
      cityId: addr.cityId ?? undefined,
      stateName: addr.stateName ?? null,
      cityName: addr.cityName ?? null,
    };
  };

  const rows: Array<{ prefix: AddressPrefix; label: string }> = [
    {
      prefix: 'personalAddress',
      label: t('personForm.address.personalAddress'),
    },
    { prefix: 'paymentAddress', label: t('personForm.address.paymentAddress') },
  ];

  const hasPersonalAddress = !!(
    person?.personalAddress?.street || person?.personalAddress?.zipCode
  );

  return (
    <div className="flex flex-col gap-xxs">
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        {rows.map(({ prefix, label }) => (
          <AddressCard
            key={prefix}
            prefix={prefix}
            label={label}
            person={person}
            control={control}
            onEdit={() => openModal(prefix)}
            onClear={() => void handleClear(prefix)}
            isDeleting={addresses.isPending}
          />
        ))}
      </div>

      {hasPersonalAddress && (
        <div className="flex justify-start">
          <Button
            hierarchy="tertiary"
            size="sm"
            type="button"
            trailingIcon={ArrowRightIcon}
            onClick={() => void handleCopyToPayment()}
            isLoading={addresses.isCopyingToPayment}
          >
            {t('personForm.address.copyToPayment')}
          </Button>
        </div>
      )}

      <AddressModal
        open={modalOpen}
        prefix={modalPrefix}
        defaultValues={modalPrefix ? getDefaultValues(modalPrefix) : EMPTY_ADDRESS}
        onClose={closeModal}
        onSave={(prefix, values) => void handleSave(prefix, values)}
      />
    </div>
  );
}
